import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Gift, Mail, Sparkles } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { ConversionBand } from "@/components/site/ConversionBand";
import { Reveal } from "@/hooks/useReveal";

const BUNDLE = [
  {
    icon: Gift,
    title: "The MAKECHANGE Playbook",
    desc: "A printable 10-pillar PDF of Dr. Sully's locked leadership framework. The same blueprint he uses with executive coaching clients.",
    badge: "PDF",
  },
  {
    icon: Mail,
    title: "7-day Make Change Mini-Course",
    desc: "A daily email series that walks you through each pillar with a 5-minute action — designed to install the framework into your week.",
    badge: "Email Series",
  },
  {
    icon: BookOpen,
    title: "Built On Purpose · Free Chapter",
    desc: "The opening chapter of Dr. Sully's leadership book — Healing Before Scale: why most leaders build on a broken blueprint.",
    badge: "eBook Sample",
  },
];

const Free = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageMeta
      title="Free MAKECHANGE Starter Kit · Dr. Sully Speaks"
      description="Free MAKECHANGE Playbook PDF, 7-day mini-course, and a sample chapter of Built On Purpose. The same framework Dr. Sully uses with 500+ leaders."
      path="/free"
    />
    <SiteNav />

    <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-hero overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,hsl(var(--gold)/0.1),transparent_50%),radial-gradient(circle_at_80%_70%,hsl(var(--orange)/0.08),transparent_50%)]"
      />
      <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal variant="up">
          <div className="inline-flex items-center gap-2 bg-orange/10 border border-orange/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-orange">
            <Sparkles className="h-3 w-3" /> Free · 3 Teasers · Instant Delivery
          </div>
          <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.02]">
            Steal the <span className="text-gradient-gold">framework</span> 500+ leaders use.
          </h1>
          <p className="text-silver text-base md:text-lg mt-6 max-w-xl leading-relaxed">
            One email gets you the MAKECHANGE Playbook PDF, a 7-day implementation
            mini-course, and a free chapter of <em>Built On Purpose</em>. No upsell
            wall. No credit card. Just the work.
          </p>

          <ul className="mt-8 space-y-5">
            {BUNDLE.map(({ icon: Icon, title, desc, badge }) => (
              <li key={title} className="flex gap-4">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold border border-gold/30">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display uppercase text-white text-base tracking-wide">
                      {title}
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.25em] bg-gold/10 text-gold px-2 py-0.5">
                      {badge}
                    </span>
                  </div>
                  <p className="text-silver text-sm mt-1.5 leading-relaxed max-w-md">
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal variant="scale" delay={120}>
          <LeadMagnetForm sourcePage="free-landing" />
        </Reveal>
      </div>
    </section>

    <ConversionBand
      eyebrow="When You're Ready"
      heading="The work compounds."
      subheading="Once the playbook lands, the next step is the Free Scan — a 2-minute diagnostic that maps your expertise into a fundable program lane."
      primaryLabel="Take the Scan"
      primaryHref="/scan"
      secondaryLabel="See Pricing"
      secondaryHref="/invest"
    />

    <div className="container py-10 text-center">
      <Link
        to="/"
        className="text-silver text-xs uppercase tracking-[0.25em] hover:text-white inline-flex items-center gap-2"
      >
        <ArrowLeft className="h-3 w-3" /> Back to home
      </Link>
    </div>

    <SiteFooter />
  </div>
);

export default Free;
